import { Button } from "antd";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useView } from "../contexts/ViewContext";
import styles from "./Close.module.css";

function Close({ learningJourneyId, view }) {
    const { setOnAdvance, advance, advancing } = useView();
    const navigate = useNavigate();
    const { weight, topic_name, closed_name, capability_sentence, forward_sentence, goal, next_name } =
        view.view_data;
    const isColdStart = weight === "cold_start";
    const isTopicOrAbove = weight === "topic" || weight === "final";

    useEffect(() => {
        // Nothing to mark complete yet at the cold start -- there is no
        // LearningStep/SubtopicClose behind this screen, just the journey.
        if (isColdStart) return;

        const viewId = view.view_id;
        setOnAdvance(async () => {
            try {
                await api.post(`/workspace/close/mark-complete/`, {
                    learning_journey_id: learningJourneyId,
                    view_id: viewId,
                });
            } catch (error) {
                console.error("Failed to mark close complete:", error);
            }
        });
    }, [isColdStart, view.view_id, learningJourneyId, setOnAdvance]);

    // Unregister on unmount so a later, unrelated view's "Continue" click
    // can never fire this close's completion call.
    useEffect(() => () => setOnAdvance(null), [setOnAdvance]);

    const finishJourney = async () => {
        try {
            await api.post(`/workspace/close/mark-complete/`, {
                learning_journey_id: learningJourneyId,
                view_id: view.view_id,
            });
        } catch (error) {
            console.error("Failed to mark close complete:", error);
        }
        navigate(`/t/${learningJourneyId}/plan`);
    };

    return (
        <div className={styles.container}>
            <div className={styles.block}>
                <span className={styles.label}>
                    {isColdStart ? "Your plan is set" : `You finished${topic_name ? ` · ${topic_name}` : ""}`}
                </span>
                <h1 className={styles.unit}>{closed_name}</h1>
                {!isColdStart && capability_sentence && (
                    <p className={styles.capability}>{capability_sentence}</p>
                )}
                {(isColdStart || isTopicOrAbove) && goal && <p className={styles.goal}>{goal}</p>}
            </div>

            <div className={styles.hairline} />

            <div className={styles.block}>
                <span className={styles.label}>Next</span>
                {next_name && <h2 className={styles.nextUnit}>{next_name}</h2>}
                {weight === "topic" && forward_sentence && (
                    <p className={styles.forward}>{forward_sentence}</p>
                )}
            </div>

            {weight === "final" ? (
                <Button type="primary" size="large" className={styles.action} onClick={finishJourney}>
                    See where you got to
                </Button>
            ) : (
                <Button
                    type="primary"
                    size="large"
                    className={styles.action}
                    onClick={advance}
                    loading={advancing}
                >
                    {isColdStart ? "Begin" : "Keep going"}
                </Button>
            )}

            {isTopicOrAbove && (
                <Link to={`/t/${learningJourneyId}/plan`} className={styles.planLink}>
                    View your plan
                </Link>
            )}
        </div>
    );
}

export default Close;
